import { motion } from "framer-motion";
import { AlertTriangle, ArrowDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export default function ProblemStatement() {
  const { t } = useLanguage();
  return (
    <section id="problem" data-testid="problem-section" className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#EE1D25]/3 rounded-full blur-[250px]" />
      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-2 border border-[#1F2937] bg-[#0A0F1A]/60">
            <AlertTriangle size={14} className="text-[#EE1D25]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] font-mono">{t("problem.badge")}</span>
          </div>

          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-[#F3F4F6] tracking-tight leading-tight mb-8">
            {t("problem.headlinePre")}<span className="text-[#EE1D25]">{t("problem.headlineAccent")}</span>{t("problem.headlineSuf")}
          </h2>

          <p className="text-base md:text-lg text-[#9CA3AF] leading-relaxed max-w-2xl mx-auto mb-10">
            {t("problem.body")}
          </p>

          <div className="grid grid-cols-3 gap-px bg-[#1F2937] max-w-xl mx-auto">
            {[
              { level: "BSL-2", count: "10,000+", label: t("problem.bsl.l2") },
              { level: "BSL-3", count: "~1,500", label: t("problem.bsl.l3") },
              { level: "BSL-4", count: "51", label: t("problem.bsl.l4") },
            ].map((item, i) => (
              <motion.div
                key={item.level}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#050505] py-6 px-4"
              >
                <p className="font-heading text-2xl md:text-3xl font-black text-[#F3F4F6]">{item.count}</p>
                <p className="text-xs text-[#EE1D25] font-medium mt-1">{item.level}</p>
                <p className="text-[10px] text-[#6B7280] mt-0.5">{item.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-12"
          >
            <p className="text-sm text-[#6B7280] mb-3">{t("problem.cta")}</p>
            <ArrowDown size={18} className="mx-auto text-[#EE1D25] animate-bounce" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
